import {useGSAP} from '@gsap/react'
import {debugLogger} from '@repo/shared/utils/debugLogger'
import gsap from 'gsap'
import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {TransitionStatus} from 'react-transition-state'
import FrameStore from '../Store/FrameStore'
import './Minimap.styles.css'
import {ISlotData, MapItem} from './components/MapItem'

const TICK_TIME = 100

const fStore = new FrameStore()

interface IMinimapProps {
  transitionStatus: TransitionStatus
}

export const Minimap: FC<IMinimapProps> = ({transitionStatus}) => {
  const updateIntervaltimer = useRef<NodeJS.Timeout | null>()

  const [slot1, setSlot1] = useState<ISlotData | null>(null)
  const [slot2, setSlot2] = useState<ISlotData | null>(null)
  const [slot3, setSlot3] = useState<ISlotData | null>(null)
  const [slot4, setSlot4] = useState<ISlotData | null>(null)
  const [slot5, setSlot5] = useState<ISlotData | null>(null)

  const enterTimeline = useRef<gsap.core.Timeline>(gsap.timeline({paused: true}))
  const exitTimeline = useRef<gsap.core.Timeline>(gsap.timeline({paused: true}))

  const minimapRef = useRef<HTMLDivElement>(null)

  const tickHandler = useCallback(() => {
    const leaderboard = fStore.getLeaderboard()?.leaderboardTeams

    if (!leaderboard?.length) {
      return
    }

    const _sorted = leaderboard.sort((a, b) => b.percentage - a.percentage)

    for (let i = 0; i < _sorted.length; i++) {
      const item = _sorted[i]

      if (!item) {
        continue
      }

      if (item.slot === 1) {
        setSlot1({...item, position: i + 1})
      } else if (item.slot === 2) {
        setSlot2({...item, position: i + 1})
      } else if (item.slot === 3) {
        setSlot3({...item, position: i + 1})
      } else if (item.slot === 4) {
        setSlot4({...item, position: i + 1})
      } else if (item.slot === 5) {
        setSlot5({...item, position: i + 1})
      }
    }
  }, [])

  useEffect(() => {
    updateIntervaltimer.current = setInterval(tickHandler, TICK_TIME)

    return () => {
      clearInterval(updateIntervaltimer.current!)
    }
  }, [])

  useGSAP(() => {
    /**
     * Enter animation
     */
    enterTimeline.current.set(minimapRef.current, {translateX: 80})

    enterTimeline.current.to(minimapRef.current, {
      translateX: 0,
      duration: 0.3,
      ease: 'back.out',
    })

    /**
     * Exit timeline
     */

    exitTimeline.current.to(minimapRef.current, {
      translateX: 80,
      duration: 0.3,
      ease: 'back.in',
    })
  })

  /**
   * Enter and leave states
   */
  useEffect(() => {
    if (transitionStatus === 'entered') {
      debugLogger({
        message: 'Enter state',
        tag: 'Minimap',
      })
      enterTimeline.current.play()
    } else if (transitionStatus === 'exiting') {
      debugLogger({
        message: 'Exit state',
        tag: 'Minimap',
      })
      enterTimeline.current.pause()
      exitTimeline.current.play()
    }
  }, [transitionStatus])

  return (
    <div id="minimap" className="fixed inset-0 z-50 hidden overflow-hidden md:block">
      <div
        ref={minimapRef}
        className="absolute bottom-[48px] right-[36px] top-[48px] flex w-[24px] flex-col items-center rounded-[4px] bg-[#ffffffb8]"
      >
        <div className="goal-area h-[40px] w-full" />
        <div className="h-[4px] w-full bg-[#ff0800]" />
        <div className="relative w-[1px] flex-1">
          {slot1 && <MapItem slotData={slot1} />}
          {slot2 && <MapItem slotData={slot2} />}
          {slot3 && <MapItem slotData={slot3} />}
          {slot4 && <MapItem slotData={slot4} />}
          {slot5 && <MapItem slotData={slot5} />}
        </div>
      </div>
    </div>
  )
}
